import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Shield, Star, Award, Play } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/company-info/`);
        setCompanyInfo(response.data);
      } catch (error) {
        console.error('Error fetching company info:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyInfo();
  }, []);

  const heroImages = [
    "https://customer-assets.emergentagent.com/job_carshop-upgrade/artifacts/o8gus0fn_236DF072-2138-49DE-8D90-4E4B72C41337.png",
    "https://customer-assets.emergentagent.com/job_carshop-upgrade/artifacts/xp7mgy6z_00964E33-5AB4-486C-B6A5-603F04F1AF42.png",
    "https://customer-assets.emergentagent.com/job_carshop-upgrade/artifacts/idu4w22l_FF3CF7BC-E425-4AC7-8166-8167A55A2870.png"
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroImages.length) % heroImages.length);
  };

  // Auto-advance slides
  useEffect(() => {
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, []);

  if (loading) {
    return (
      <section className="relative h-screen bg-luxury-black flex items-center justify-center">
        <motion.div 
          className="text-luxury-gold text-xl"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          טוען...
        </motion.div>
      </section>
    );
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  return (
    <section id="home" className="relative h-screen overflow-hidden bg-luxury-black">
      {/* Background Image Slider with Ken Burns */}
      <div className="absolute inset-0">
        <AnimatePresence mode="wait">
          {heroImages.map((image, index) => (
            index === currentSlide && (
              <motion.div
                key={index}
                className="absolute inset-0"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.5 }}
              >
                <motion.img
                  src={image}
                  alt={`Hero ${index + 1}`}
                  className="w-full h-full object-cover"
                  initial={{ scale: 1 }}
                  animate={{ scale: 1.1 }}
                  transition={{ duration: 10, ease: "linear" }}
                />
              </motion.div>
            )
          ))}
        </AnimatePresence>
      </div>

      {/* Gradient Overlays - Light version */}
      <div className="absolute inset-0 bg-gradient-to-r from-luxury-black/40 via-transparent to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-luxury-black/60 via-transparent to-transparent" />
      
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-luxury-gold/5 to-transparent pointer-events-none" />
      
      {/* Content */}
      <div className="relative z-10 flex items-center h-full">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="max-w-3xl"
          >
            {/* Tagline */}
            <motion.div 
              variants={itemVariants}
              className="flex items-center gap-3 mb-6"
            >
              <div className="h-px w-12 bg-luxury-gold" />
              <span className="text-luxury-gold text-sm font-medium tracking-[0.3em] uppercase">
                מכירה - יבוא רכבי יוקרה - טרייד אין
              </span>
            </motion.div>

            {/* Main Title */}
            <motion.h1 
              variants={itemVariants}
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-luxury-text mb-10 leading-[1.1]"
            >
              {companyInfo?.heroTitle || 'הרכב המושלם'}
              <br />
              <span className="gold-shimmer">לכל אחד</span>
            </motion.h1>

            {/* Trust Indicators */}
            <motion.div 
              variants={itemVariants}
              className="flex flex-wrap gap-6 mb-10"
            >
              {[
                { icon: Shield, text: 'שירות אמין' },
                { icon: Star, text: 'ליווי מקצועי' },
                { icon: Award, text: 'רכבים איכותיים' }
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-2 text-luxury-text/70">
                  <item.icon className="w-5 h-5 text-luxury-gold" />
                  <span className="text-sm font-medium">{item.text}</span>
                </div>
              ))}
            </motion.div>

            {/* CTA Buttons */}
            <motion.div 
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-4"
            >
              <motion.button
                onClick={() => document.getElementById('cars')?.scrollIntoView({ behavior: 'smooth' })}
                className="btn-luxury text-base px-8 py-4 gold-glow-hover"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                צפה ברכבים שלנו
              </motion.button>
              <motion.button
                onClick={() => window.location.href = 'tel:0587019669'}
                className="group flex items-center justify-center gap-3 px-8 py-4 border border-luxury-border text-luxury-text hover:border-luxury-gold hover:text-luxury-gold transition-all duration-300"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="font-medium">צור קשר עכשיו</span>
              </motion.button>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Navigation Arrows */}
      <div className="absolute bottom-1/2 translate-y-1/2 left-4 right-4 flex justify-between pointer-events-none z-20">
        <motion.button
          onClick={prevSlide}
          className="pointer-events-auto w-12 h-12 flex items-center justify-center bg-luxury-black/50 hover:bg-luxury-gold text-luxury-text hover:text-black transition-all duration-300 border border-luxury-border/50 hover:border-luxury-gold"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <ChevronRight className="w-6 h-6" />
        </motion.button>
        <motion.button
          onClick={nextSlide}
          className="pointer-events-auto w-12 h-12 flex items-center justify-center bg-luxury-black/50 hover:bg-luxury-gold text-luxury-text hover:text-black transition-all duration-300 border border-luxury-border/50 hover:border-luxury-gold"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <ChevronLeft className="w-6 h-6" />
        </motion.button>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3 z-20">
        {heroImages.map((_, index) => (
          <motion.button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`h-1 transition-all duration-500 ${
              index === currentSlide 
                ? 'w-12 bg-luxury-gold' 
                : 'w-6 bg-luxury-text/30 hover:bg-luxury-text/50'
            }`}
            whileHover={{ scale: 1.2 }}
          />
        ))}
      </div>

      {/* Scroll Indicator */}
      <motion.div 
        className="absolute bottom-8 right-8 hidden md:flex flex-col items-center gap-2 text-luxury-muted"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <span className="text-xs tracking-widest uppercase rotate-90 origin-center">גלול</span>
      </motion.div>
    </section>
  );
};

export default Hero;
